import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  Button,
  Paper,
  Divider,
  Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import colors from 'colors/color';
import NewAppBar from 'components/Navbars/AppBar';
import { trackBlogView } from 'utils/analytics';

// Import blog images
import blog1Image from '../../assets/img/blog_1.png';
import blog2Image from '../../assets/img/blog_2.png';
import blog3Image from '../../assets/img/blog_3.png';
import blog4Image from '../../assets/img/blog_4.png';
import blog5Image from '../../assets/img/blog_5.png';
import blog6Image from '../../assets/img/blog_6.png';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: colors.surface,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(6),
}));

const ArticleHeader = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '400px',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  borderRadius: '16px',
  overflow: 'hidden',
  marginBottom: theme.spacing(4),
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 100%)',
  },
}));

const ArticleContent = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '16px',
  backgroundColor: colors.white,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3),
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  color: colors.primary,
  textTransform: 'none',
  fontFamily: 'Poppins',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: 'rgba(0, 23, 231, 0.04)',
  },
}));

const CTASection = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(6),
  padding: theme.spacing(4),
  borderRadius: '16px',
  background: `linear-gradient(135deg, ${colors.primary} 0%, #0085FF 100%)`,
  color: colors.white,
  textAlign: 'center',
}));

// Blog articles data with full content
const articlesData = {
  'erreurs-entretien-embauche': {
    id: 1,
    title: "Les 10 erreurs à éviter lors d'un entretien d'embauche",
    slug: 'erreurs-entretien-embauche',
    description: "Les 10 erreurs les plus fréquentes en entretien, et surtout comment les éviter avec des exemples concrets.",
    image: blog1Image,
    readTime: '6–8 min',
    audience: 'Candidats',
    content: {
      introduction: "Un entretien n'est pas un interrogatoire. C'est une évaluation de compétences, de méthode de travail et de fit. Beaucoup de candidats échouent non pas par manque de niveau, mais par manque de clarté, de structure, ou parce qu'ils donnent de mauvais signaux. Voici les 10 erreurs qui coûtent le plus cher… et les correctifs simples.",
      sections: [
        {
          title: "1) Arriver sans comprendre le poste",
          content: "Erreur : réciter son parcours sans faire le lien avec le besoin.\n\nCorrectif : résume le poste en 30 secondes (\"si j'ai bien compris, vous cherchez X pour résoudre Y…\") puis explique comment tu réponds à ça."
        },
        {
          title: "2) Ne pas préparer de preuve (exemples chiffrés)",
          content: "Erreur : rester vague (\"je suis autonome\", \"j'ai géré des projets\").\n\nCorrectif : prépare 3 preuves : contexte → action → résultat.\n\nExemple : \"J'ai réduit le délai de livraison de 20% en standardisant la phase de QA.\""
        },
        {
          title: "3) Parler trop, répondre trop long",
          content: "Erreur : tu noies l'essentiel, tu fatigues le recruteur.\n\nCorrectif : structure en 3 étapes : réponse courte → exemple → conclusion."
        },
        {
          title: "4) Critiquer ses anciens employeurs",
          content: "Erreur : tu sembles conflictuel et difficile à manager.\n\nCorrectif : reformule en apprentissage : \"J'ai appris que je fonctionne mieux dans un cadre où…\""
        },
        {
          title: "5) Ne pas maîtriser son pitch",
          content: "Erreur : \"euh… j'ai fait un peu de tout\".\n\nCorrectif (pitch 45 sec) :\n• Qui je suis (poste actuel/dernier)\n• Mon expertise (2–3 compétences)\n• Mon impact (1 résultat)\n• Pourquoi je candidate (1 phrase)"
        },
        {
          title: "6) Ne pas connaître son CV",
          content: "Erreur : incohérences sur dates, rôles, technologies.\n\nCorrectif : relis ton CV comme si tu étais le recruteur : tout ce qui n'est pas clair sera questionné."
        },
        {
          title: "7) Manquer de curiosité (aucune question)",
          content: "Erreur : tu sembles passif.\n\nCorrectif : prépare 5 questions."
        },
        {
          title: "8) Se vendre \"trop\" ou \"pas assez\"",
          content: "Erreur : arrogance ou effacement.\n\nCorrectif : parle en faits et en résultats, pas en \"qualités\"."
        },
        {
          title: "9) Négliger les signaux non-verbaux",
          content: "Erreur : posture fermée, regard fuyant, ton monotone.\n\nCorrectif : rythme lent, voix posée, phrases courtes. L'objectif : être clair, pas impressionner."
        },
        {
          title: "10) Ne pas cadrer la question du salaire",
          content: "Erreur : répondre au hasard, ou trop tôt, ou trop tard.\n\nCorrectif : donne une fourchette justifiée + conditions :\n\"Sur le marché, pour ce périmètre, je vise X–Y selon responsabilités, variable et télétravail.\""
        },
        {
          title: "5 questions intelligentes à poser",
          content: "1. \"À quoi ressemble une réussite à 3 mois ?\"\n2. \"Quels sont les 2 principaux défis de ce rôle ?\"\n3. \"Comment mesurez-vous la performance ici ?\"\n4. \"Pourquoi le poste est ouvert (croissance, remplacement) ?\"\n5. \"Quelles sont les prochaines étapes du process ?\""
        }
      ],
      conclusion: "Un entretien se gagne avec structure, preuves, et clarté. Si tu veux maximiser tes chances, prépare tes réponses au format Situation → Action → Résultat et tes questions à l'avance."
    }
  },
  'cv-percutant-ats': {
    id: 2,
    title: 'Comment rédiger un CV percutant (et lisible par les ATS)',
    slug: 'cv-percutant-ats',
    description: 'Un CV clair, orienté impact, compatible ATS : structure, sections, erreurs à éviter, exemples.',
    image: blog2Image,
    readTime: '8–10 min',
    audience: 'Candidats',
    content: {
      introduction: "Un CV sert à décrocher un entretien, pas à raconter ta vie. Il doit être : scannable en 10 secondes, compatible ATS (logiciels de tri), et orienté résultats.",
      sections: [
        {
          title: "Structure recommandée (1 page si < 8–10 ans d'expérience)",
          content: "1. En-tête : Nom + rôle + ville + email + LinkedIn\n2. Résumé (4 lignes max)\n3. Compétences (8–12 mots-clés)\n4. Expérience (impact d'abord)\n5. Formation + certifs\n6. Projets / publications (si utile)"
        },
        {
          title: "Résumé (4 lignes) : modèle",
          content: "\"Profil [métier] avec [X] années en [domaine].\nSpécialisé en [2–3 compétences].\nRésultats : [1 impact chiffré].\nJe recherche [type de poste] dans [secteur].\""
        },
        {
          title: "Expérience : écris \"impact\", pas \"missions\"",
          content: "Mauvais : \"Gestion d'une équipe / suivi des tâches / reporting.\"\n\nBon :\n• \"Piloté une équipe de 5 personnes ; +18% de vélocité en 2 mois.\"\n• \"Automatisé le reporting ; -6h/semaine économisées.\"\n\nRègle : 3–5 bullets par poste, chaque bullet = verbe d'action + résultat."
        },
        {
          title: "ATS : ce que tu dois absolument faire",
          content: "• Éviter tableaux, colonnes, icônes, logos\n• Police standard (Inter, Arial, Calibri)\n• Titres simples : \"Experience\", \"Education\", \"Skills\"\n• Sauvegarder en PDF (ou DOCX si demandé)\n• Ajouter les mots-clés du job (sans tricher)"
        },
        {
          title: "Les 7 erreurs qui détruisent un CV",
          content: "1. Un titre flou (\"Profil polyvalent\")\n2. Trop de texte, pas de chiffres\n3. Un \"niveau\" de langue non crédible\n4. Un CV design illisible (ATS-friendly = simple)\n5. Des dates incohérentes\n6. Des compétences sans preuve\n7. Une page \"hobbies\" plus longue que l'impact pro"
        }
      ],
      conclusion: "Un bon CV, c'est un document de vente. Court, clair, orienté résultats."
    }
  },
  'questions-entretien-candidat': {
    id: 3,
    title: 'Les meilleures questions à poser à un candidat en entretien',
    slug: 'questions-entretien-candidat',
    description: 'Questions efficaces pour évaluer compétences, méthode, comportement et fit. Scorecards incluses.',
    image: blog3Image,
    readTime: '7–9 min',
    audience: 'Recruteurs',
    content: {
      introduction: "Un entretien non structuré mesure surtout : aisance, storytelling, biais de similarité. Pas la performance future. Il faut donc poser des questions prédictives et comparables.",
      sections: [
        {
          title: "1) Questions \"preuve\" (passé réel)",
          content: "• \"Donne-moi un projet dont tu es fier : objectif, ton rôle, résultat.\"\n• \"Un échec : qu'est-ce que tu aurais fait différemment ?\"\n• \"Dernière décision difficile : comment tu as tranché ?\""
        },
        {
          title: "2) Questions \"méthode\" (comment il travaille)",
          content: "• \"Quand tu ne sais pas, que fais-tu en premier ?\"\n• \"Comment tu estimes une tâche et gères les imprévus ?\"\n• \"Comment tu priorises quand tout est urgent ?\""
        },
        {
          title: "3) Questions \"collaboration\"",
          content: "• \"Un désaccord avec un stakeholder : comment tu l'as géré ?\"\n• \"Comment tu donnes/reçois du feedback ?\"\n• \"Ton manager idéal : qu'est-ce qu'il fait / ne fait pas ?\""
        },
        {
          title: "4) Questions \"exercice\" (job-related)",
          content: "• Cas rapide : \"Voilà un brief incomplet. Quelles questions poses-tu ?\"\n• Pour un commercial : \"Écris un mail de prospection sur ce contexte.\"\n• Pour un dev : \"Explique comment tu débugges ce bug.\""
        },
        {
          title: "5) Questions \"alignement\"",
          content: "• \"Qu'est-ce qui te ferait quitter ce poste dans 6 mois ?\"\n• \"Qu'est-ce qui te motive concrètement ici ?\"\n• \"Quel environnement te fait performer (et lequel te bloque) ?\""
        },
        {
          title: "Mini scorecard (simple et efficace)",
          content: "Note 1–5 sur :\n• Compétence technique / métier\n• Résolution de problèmes\n• Communication & clarté\n• Ownership (autonomie, fiabilité)\n• Collaboration\n• Motivation / alignement"
        }
      ],
      conclusion: "Plus l'entretien est structuré, plus tu recrutes juste et vite."
    }
  },
  'optimiser-linkedin-recruteurs': {
    id: 4,
    title: 'Optimiser son profil LinkedIn pour attirer les recruteurs',
    slug: 'optimiser-linkedin-recruteurs',
    description: 'Headline, About, expériences, mots-clés, preuves : la méthode pour rendre ton profil trouvable et crédible.',
    image: blog4Image,
    readTime: '7–9 min',
    audience: 'Candidats',
    content: {
      introduction: "LinkedIn n'est pas un CV. C'est un moteur de recherche + une vitrine de crédibilité.",
      sections: [
        {
          title: "1) Headline : arrête les titres vagues",
          content: "Mauvais : \"Open to work | Passionné\"\n\nBon : \"Data Analyst | SQL, Power BI | Automatisation & dashboards | Retail/Finance\""
        },
        {
          title: "2) Photo + bannière : cohérence simple",
          content: "Photo : pro, fond neutre, visage visible.\nBannière : 1 phrase + 3 compétences max (pas de slogans)."
        },
        {
          title: "3) About : modèle court (8 lignes)",
          content: "• Qui tu es (métier + séniorité)\n• 2 compétences différenciantes\n• 1–2 résultats chiffrés\n• Ce que tu cherches\n• CTA : \"Contact : email…\""
        },
        {
          title: "4) Expériences : écris comme un cas client",
          content: "Chaque poste doit contenir :\n• Contexte (1 ligne)\n• Responsabilités (2 lignes)\n• 3 résultats chiffrés"
        },
        {
          title: "5) Compétences : 30 max, validées",
          content: "Choisis les mots-clés que les recruteurs tapent.\nFais valider les 3 principales."
        },
        {
          title: "6) Preuves : portfolio, posts, recommandations",
          content: "• 2 recommandations solides valent plus que 10 compétences.\n• Ajoute un projet / PDF / lien."
        },
        {
          title: "7) Stratégie simple : 15 minutes par semaine",
          content: "• 2 commentaires utiles sur des posts de ton domaine\n• 1 post court toutes les 2 semaines : \"problème → solution → apprentissage\""
        }
      ],
      conclusion: "Le profil LinkedIn efficace est clair, riche en preuves, et optimisé mots-clés."
    }
  },
  'competences-cv-preuves': {
    id: 5,
    title: 'Les compétences clés à mettre en avant sur un CV',
    slug: 'competences-cv-preuves',
    description: 'Hard skills, soft skills, preuves, formulations : comment choisir et démontrer tes compétences.',
    image: blog5Image,
    readTime: '6–8 min',
    audience: 'Candidats',
    content: {
      introduction: "Lister des compétences sans preuve ne vaut presque rien. Ce qui compte : la démonstration.",
      sections: [
        {
          title: "1) Hard skills : choisis 8–12 max",
          content: "Exemples (selon métier) :\n• Data : SQL, Python, Power BI, dbt\n• Dev : React, Node, Symfony, CI/CD\n• Marketing : SEO, Ads, HubSpot, GA4\n• PM : discovery, roadmap, user research\n\nRègle : si tu l'écris, tu dois pouvoir le défendre en entretien."
        },
        {
          title: "2) Soft skills : 3 max, prouvées par des faits",
          content: "Les soft skills crédibles sont celles que tu illustres :\n• Ownership : \"projet repris en urgence, livré avant deadline\"\n• Communication : \"alignement hebdo + réduction des malentendus\"\n• Leadership : \"mentoring de 2 juniors\""
        },
        {
          title: "3) Le format preuve (à copier)",
          content: "\"[Compétence] — appliquée sur [contexte], résultat : [impact chiffré].\""
        },
        {
          title: "4) Où les mettre dans le CV",
          content: "• Section \"Skills\" (mots-clés)\n• Dans chaque expérience (preuves)\n• Dans les projets (démonstration)"
        },
        {
          title: "5) Ce qui impressionne réellement",
          content: "• Réduction de coûts / délais\n• Augmentation de revenus / conversion\n• Automatisation\n• Fiabilité / qualité\n• Capacité à clarifier un besoin flou"
        }
      ],
      conclusion: "Ce n'est pas la liste qui recrute, c'est la preuve."
    }
  },
  'recrutement-moderne-2026': {
    id: 6,
    title: 'Recrutement moderne en 2026 : process, data, IA… sans perdre l\'humain',
    slug: 'recrutement-moderne-2026',
    description: 'Un cadre concret pour recruter mieux : scorecards, sourcing, automatisation, IA, expérience candidat.',
    image: blog6Image,
    readTime: '8–10 min',
    audience: 'RH / Dirigeants',
    content: {
      introduction: "Deux extrêmes coexistent : recrutement \"au feeling\" → erreurs, biais, turnover et recrutement \"trop outillé\" → process lourd, candidats perdus. Le recrutement moderne, c'est un process léger, mesurable, et humain.",
      sections: [
        {
          title: "1) Le socle : scorecard avant le sourcing",
          content: "Avant de publier une annonce, définis :\n• 5 critères essentiels (pas 15)\n• preuves attendues (ex : \"a déjà livré X\")\n• niveau minimum / niveau idéal\n\nSans scorecard, tu recrutes au hasard."
        },
        {
          title: "2) Sourcing : qualité > volume",
          content: "3 canaux efficaces :\n• Réseau + referrals\n• LinkedIn (approche personnalisée)\n• Vivier interne (anciens candidats)\n\nRègle : un message doit montrer que tu as compris le profil."
        },
        {
          title: "3) Process recommandé (rapide et prédictif)",
          content: "1. Screening 15 min (clarifier + salaire + dispo)\n2. Entretien structuré 45 min (preuves)\n3. Exercice job-related (60–90 min max)\n4. Entretien final (alignement + offre)"
        },
        {
          title: "4) Data : 5 KPI qui comptent vraiment",
          content: "• Time-to-hire\n• Source of hire (qualité)\n• Taux d'acceptation de l'offre\n• Taux d'abandon candidats\n• Rétention 90 jours (qualité réelle)"
        },
        {
          title: "5) IA : où ça aide (et où ça peut nuire)",
          content: "Utile pour :\n• résumé de CV\n• extraction de compétences\n• suggestions de questions / scorecards\n• tri initial selon critères définis\n\nDangereux si :\n• tu laisses l'IA décider sans cadre\n• tu ne contrôles pas les biais\n• tu confonds \"matching\" et performance"
        },
        {
          title: "6) Expérience candidat : le levier sous-estimé",
          content: "Un bon candidat veut :\n• clarté sur les étapes\n• feedback minimum\n• délais tenus\n• respect de son temps"
        }
      ],
      conclusion: "Le recrutement moderne, c'est de la rigueur opérationnelle au service d'une décision humaine."
    }
  }
};

const BlogArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);

  useEffect(() => {
    if (slug && articlesData[slug]) {
      setArticle(articlesData[slug]);
      window.scrollTo(0, 0);
      // Track blog article view
      trackBlogView(slug, articlesData[slug].title);
    } else {
      navigate('/blogs');
    }
  }, [slug, navigate]);

  if (!article) {
    return null;
  }

  return (
  <><NewAppBar />
    <PageContainer>
      
      <Container maxWidth="lg">
        <BackButton
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blogs')}
        >
          Retour aux articles
        </BackButton>

        {/* Article Header Image */}
        <ArticleHeader
          sx={{
            backgroundImage: `url(${article.image})`,
          }}
        />

        {/* Article Content */}
        <ArticleContent>
          {/* Meta Info */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              label={article.audience}
              sx={{
                backgroundColor: colors.primary,
                color: colors.white,
                fontFamily: 'Poppins',
                fontWeight: 600,
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: colors.textSecondary }}>
              <AccessTimeIcon sx={{ fontSize: '1rem' }} />
              <Typography variant="body2" sx={{ fontFamily: 'Poppins' }}>
                {article.readTime}
              </Typography>
            </Box>
          </Box>

          {/* Title */}
          <Typography
            variant="h3"
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 800,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              color: colors.textPrimary,
              marginBottom: 3,
              lineHeight: 1.3,
            }}
          >
            {article.title}
          </Typography>

          {/* Description */}
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 400,
              color: colors.textSecondary,
              marginBottom: 4,
              lineHeight: 1.6,
            }}
          >
            {article.description}
          </Typography>

          <Divider sx={{ marginY: 4 }} />

          {/* Introduction */}
          <Typography
            variant="body1"
            sx={{
              fontFamily: 'Poppins',
              fontSize: '1.125rem',
              lineHeight: 1.8,
              color: colors.textPrimary,
              marginBottom: 4,
            }}
          >
            {article.content.introduction}
          </Typography>

          {/* Content Sections */}
          {article.content.sections.map((section, index) => (
            <Box key={index} sx={{ marginBottom: 4 }}>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: 'Poppins',
                  fontWeight: 700,
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  color: colors.primary,
                  marginBottom: 2,
                }}
              >
                {section.title}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  color: colors.textPrimary,
                  whiteSpace: 'pre-line',
                }}
              >
                {section.content}
              </Typography>
            </Box>
          ))}

          <Divider sx={{ marginY: 4 }} />

          {/* Conclusion */}
          <Box
            sx={{
              padding: 3,
              backgroundColor: colors.surface,
              borderRadius: '12px',
              borderLeft: `4px solid ${colors.primary}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 700,
                color: colors.primary,
                marginBottom: 2,
              }}
            >
              Conclusion
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Poppins',
                fontSize: '1rem',
                lineHeight: 1.8,
                color: colors.textPrimary,
              }}
            >
              {article.content.conclusion}
            </Typography>
          </Box>

          {/* CTA Section */}
          <CTASection elevation={0}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 700,
                marginBottom: 2,
              }}
            >
              Besoin d'aide avec Talenty Hire  ?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Poppins',
                marginBottom: 3,
                opacity: 0.95,
              }}
            >
              Talenty Hire  vous aide à structurer l'évaluation (scorecards, shortlists, feedback) 
              et à éviter les décisions "au feeling".
            </Typography>
            <Button
              variant="contained"
              sx={{
                backgroundColor: colors.white,
                color: colors.primary,
                fontFamily: 'Poppins',
                fontWeight: 600,
                padding: '12px 32px',
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: colors.surface,
                },
              }}
              onClick={() => navigate('/jobs')}
            >
              Découvrir nos offres
            </Button>
          </CTASection>
        </ArticleContent>
      </Container>
    </PageContainer></>
  );
};

export default BlogArticle;
